/* istanbul ignore file */
import Konva from 'konva';
import { RectConfig } from 'konva/types/shapes/Rect';
import { Rect as Rectangle, KonvaNodeEvents, Group } from 'react-konva';
import { useCallback, forwardRef, useRef, useState } from 'react';
import { CANVAS_INTERACTION_STATE } from 'utils/konva/types';
import { observer } from 'mobx-react-lite';
import { Label, RegionLabel, RegionLabelProps, LabelOrientationEnum } from 'components/Canvas/RegionLabel';
import { isNearRectBorder, setCursor } from 'components/Canvas/helpers';

export interface RectProps extends Omit<RectConfig, 'scale'>, KonvaNodeEvents {
  labels?: Label[];
  color?: string;
  labelProps?: Partial<RegionLabelProps>;
  isPredicted?: boolean;
  fillEnabled?: boolean;
  zoomScale?: number;
  interactionState?: CANVAS_INTERACTION_STATE;
  isShapeSelected?: boolean;
  isHovered?: boolean;
}
export const CHECK_MARK_OFFSET = 20;
/**
 * PLEASE don't add any random props to this component. It already extends from ALL properties that
 * react-konva Rect allows. So you can use this as if it's Rect from react-konva and give any
 * konva specific properties on the parent; wherever you call this component.
 */
const RectComponent: React.FC<RectProps> = forwardRef<Konva.Rect, RectProps>(
  (
    {
      children,
      labels,
      zoomScale = 1,
      scale,
      color,
      onMouseMove,
      onMouseLeave,
      opacity,
      labelProps,
      isPredicted,
      interactionState = CANVAS_INTERACTION_STATE.IDLE,
      fillEnabled,
      ...konvaProps
    },
    ref,
  ) => {
    const labelContainerRef = useRef<Konva.Group>(null);
    const [isMouseOver, setIsMouseOver] = useState(false);

    const handleContainerMouseEnter = useCallback(() => {
      setIsMouseOver(true);
    }, []);

    const handleContainerMouseLeave = useCallback(() => {
      setIsMouseOver(false);
    }, []);

    const handleMouseMove = useCallback(
      (evt: Konva.KonvaEventObject<MouseEvent>) => {
        onMouseMove?.(evt);
        const isNearBorder = isNearRectBorder(evt);
        const cursor = isNearBorder
          ? 'pointer'
          : [CANVAS_INTERACTION_STATE.DRAWING_BOX, CANVAS_INTERACTION_STATE.DRAWING_POLYGON].includes(interactionState)
          ? 'crosshair'
          : 'default';

        setCursor(evt, cursor);
      },
      [interactionState],
    );

    const handleMouseLeave = useCallback((evt: Konva.KonvaEventObject<MouseEvent>) => {
      onMouseLeave?.(evt);
      setCursor(evt, 'auto');
    }, []);

    return (
      <Group onMouseEnter={handleContainerMouseEnter} onMouseLeave={handleContainerMouseLeave}>
        {/* Ghost element to allow users to hover over region labels with gaps. */}
        <Rectangle x={konvaProps.x} y={konvaProps.y + konvaProps.height} height={20} width={20} />
        {isMouseOver && (
          <Group ref={labelContainerRef}>
            {labels &&
              labels.map((label, index) => {
                /*
                x and y are top-left and top-right respectively. The following calculations 
                situate the label to the top-right of the region with the tops of both at equal height.
                The index is used here to push the successive labels downward relative to the first one.
                The scale of the image is crucial here for the label to stay in the same position relative to the region.
                */
                const labelOrientation = labelProps?.orientation || LabelOrientationEnum.BOTTOM;
                const orientationMap = {
                  [LabelOrientationEnum.BOTTOM]: [konvaProps.x, konvaProps.y + konvaProps.height + index * 70],
                  [LabelOrientationEnum.RIGHT]: [konvaProps.x + konvaProps.width + 10, konvaProps.y + (1 / scale) * 20 + index * (1 / scale) * 25],
                };
                const [xAxis, yAxis] = orientationMap[labelOrientation];

                return (
                  <RegionLabel
                    {...labelProps}
                    x={xAxis}
                    y={yAxis}
                    label={label}
                    color={color}
                    width={(konvaProps.width + CHECK_MARK_OFFSET / scale) as number}
                    cornerStyle="rounded"
                    scale={zoomScale * scale}
                    isPredicted={isPredicted}
                  />
                );
              })}
          </Group>
        )}
        <Rectangle
          ref={ref}
          opacity={opacity}
          strokeWidth={2.5 / zoomScale / scale}
          hitStrokeWidth={5}
          stroke={color}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          fillEnabled={fillEnabled}
          fillPatternOffsetX={konvaProps.x}
          fillPatternOffsetY={konvaProps.y}
          cornerRadius={0}
          {...(isPredicted && { dash: [5, 3] })}
          {...konvaProps}
        />
        {children}
      </Group>
    );
  },
);
export const Rect = observer(RectComponent);
